import { useCallback, useMemo, useState } from 'react';
import { STATUS_META } from '../data/projectsTable';
import { useAppSelector } from '../app/hooks';
import { selectUser } from '../entities/session/model/slice';
import {
    CreateProjectPayload,
    useCreateProjectMutation,
    useDeleteProjectMutation,
    useGetProjectsQuery,
} from '../entities/project/api/projectApi';
import type {
    AuthorFilter,
    PeriodFilter,
    PerPage,
    Project,
    SortKey,
    SortState,
    StatusCounts,
    StatusFilter,
} from '../types/projectsTable';
import { selectOrganizationId } from '../entities/organization/model/slice';
import { isDateRange } from '../types/projectsTable';

const DAY_MS = 86_400_000;
type TextSortKey = 'name' | 'author' | 'status';
const TEXT_KEYS: TextSortKey[] = ['name', 'author', 'status'];
const isTextKey = (key: SortKey): key is TextSortKey => (TEXT_KEYS as SortKey[]).includes(key);

const matchesQuery = (project: Project, query: string): boolean =>
    !query ||
    project.name.toLowerCase().includes(query) ||
    project.author.toLowerCase().includes(query);

const compare = (a: Project, b: Project, { key, dir }: SortState): number => {
    const sign = dir === 'asc' ? 1 : -1;

    if (isTextKey(key)) {
        const left = (key === 'status' ? STATUS_META[a.status].label : a[key]).toLowerCase();
        const right = (key === 'status' ? STATUS_META[b.status].label : b[key]).toLowerCase();
        return left.localeCompare(right, 'ru') * sign;
    }

    if (key === 'pos') return (a.total - b.total) * sign;
    if (key === 'price') return (a.price - b.price) * sign;
    return (new Date(a.date).getTime() - new Date(b.date).getTime()) * sign;
};

export function useProjectsTable() {
    const organizationId = useAppSelector(selectOrganizationId);

    const {
        data: projects = [],
        isLoading,
        isFetching,
    } = useGetProjectsQuery({ organizationId }, { skip: !organizationId });

    const [createProject] = useCreateProjectMutation();
    const [deleteProject] = useDeleteProjectMutation();

    const [query, setQueryValue] = useState('');
    const [status, setStatusValue] = useState<StatusFilter>('all');
    const [period, setPeriodValue] = useState<PeriodFilter>('all');
    const [author, setAuthorValue] = useState<AuthorFilter>('all');

    const [sort, setSort] = useState<SortState>({ key: 'date', dir: 'desc' });
    const [page, setPageValue] = useState(1);
    const [perPage, setPerPageValue] = useState<PerPage>(25);
    const [selected, setSelected] = useState<Set<string>>(new Set());

    /** Выборка без учёта статуса и периода — по ней считаются счётчики чипов. */
    const base = useMemo(() => {
        const needle = query.trim().toLowerCase();
        return projects.filter(
            (project) =>
                (author === 'all' || project.author === author) && matchesQuery(project, needle),
        );
    }, [projects, query, author]);

    const filtered = useMemo(() => {
        const now = Date.now();
        return base
            .filter((project) => {
                if (status !== 'all' && project.status !== status) return false;
                if (isDateRange(period)) {
                    /* Границы включительно: до конца дня «по». */
                    const created = new Date(project.date).getTime();
                    if (created < new Date(`${period.start}T00:00:00`).getTime()) return false;
                    if (created > new Date(`${period.end}T23:59:59`).getTime()) return false;
                } else if (period !== 'all') {
                    const days = (now - new Date(project.date).getTime()) / DAY_MS;
                    if (days > Number(period)) return false;
                }
                return true;
            })
            .sort((a, b) => compare(a, b, sort));
    }, [base, status, period, sort]);

    const counts: StatusCounts = useMemo(
        () => ({
            all: base.length,
            new: base.filter((project) => project.status === 'new').length,
            run: base.filter((project) => project.status === 'run').length,
            done: base.filter((project) => project.status === 'done').length,
        }),
        [base],
    );

    const authors = useMemo(
        () =>
            Array.from(new Set(projects.map((project) => project.author))).sort((a, b) =>
                a.localeCompare(b, 'ru'),
            ),
        [projects],
    );

    const totalPages = Math.max(1, Math.ceil(filtered.length / perPage));
    const currentPage = Math.min(page, totalPages);
    const pageStart = (currentPage - 1) * perPage;
    const pageRows = useMemo(
        () => filtered.slice(pageStart, pageStart + perPage),
        [filtered, pageStart, perPage],
    );

    const rangeLabel = filtered.length
        ? `${pageStart + 1}–${Math.min(pageStart + perPage, filtered.length)} из ${filtered.length}`
        : '0 из 0';

    const isFiltered =
        query.trim() !== '' || status !== 'all' || period !== 'all' || author !== 'all';

    /* --- фильтры: любое изменение возвращает на первую страницу --- */

    const setQuery = useCallback((value: string) => {
        setQueryValue(value);
        setPageValue(1);
    }, []);

    const setStatus = useCallback((value: StatusFilter) => {
        setStatusValue(value);
        setPageValue(1);
    }, []);

    const setPeriod = useCallback((value: PeriodFilter) => {
        setPeriodValue(value);
        setPageValue(1);
    }, []);

    const setAuthor = useCallback((value: AuthorFilter) => {
        setAuthorValue(value);
        setPageValue(1);
    }, []);

    const resetFilters = useCallback(() => {
        setQueryValue('');
        setStatusValue('all');
        setPeriodValue('all');
        setAuthorValue('all');
        setPageValue(1);
    }, []);

    /* --- сортировка и пагинация --- */

    const toggleSort = useCallback((key: SortKey) => {
        setSort((prev) =>
            prev.key === key
                ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
                : { key, dir: isTextKey(key) ? 'asc' : 'desc' },
        );
        setPageValue(1);
    }, []);

    const setPage = useCallback((value: number) => {
        setPageValue(Math.max(1, value));
    }, []);

    const setPerPage = useCallback((value: PerPage) => {
        setPerPageValue(value);
        setPageValue(1);
    }, []);

    /* --- выделение --- */

    const toggleRow = useCallback((id: string) => {
        setSelected((prev) => {
            const next = new Set(prev);
            if (!next.delete(id)) next.add(id);
            return next;
        });
    }, []);

    const togglePage = useCallback(() => {
        const ids = pageRows.map((project) => project.id);
        setSelected((prev) => {
            const next = new Set(prev);
            const allSelected = ids.every((id) => next.has(id));
            ids.forEach((id) => (allSelected ? next.delete(id) : next.add(id)));
            return next;
        });
    }, [pageRows]);

    const clearSelection = useCallback(() => setSelected(new Set()), []);

    /* --- изменение списка --- */
    
    const addProject = useCallback(
        async (values: Omit<CreateProjectPayload, 'organizationId'>): Promise<Project> => {
            const project = await createProject({ ...values, organizationId }).unwrap();
            resetFilters();
            setSort({ key: 'date', dir: 'desc' });
            return project;
        },
        [createProject, organizationId, resetFilters],
    );

    const removeProjects = useCallback(
        async (ids: string[]): Promise<void> => {
            await Promise.all(ids.map((id) => deleteProject(id).unwrap()));
            setSelected((prev) => {
                const next = new Set(prev);
                ids.forEach((id) => next.delete(id));
                return next;
            });
        },
        [deleteProject],
    );

    return {
        // данные
        projects,
        filtered,
        pageRows,
        authors,
        counts,
        isLoading,
        isFetching,
        // фильтры
        query,
        status,
        period,
        author,
        isFiltered,
        setQuery,
        setStatus,
        setPeriod,
        setAuthor,
        resetFilters,
        // сортировка
        sort,
        toggleSort,
        // пагинация
        page: currentPage,
        totalPages,
        perPage,
        rangeLabel,
        setPage,
        setPerPage,
        // выделение
        selected,
        toggleRow,
        togglePage,
        clearSelection,
        // изменение списка
        addProject,
        removeProjects,
    };
}